import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export interface ProductTourStep {
  /** Sélecteur CSS de l'élément à mettre en avant (ex: [data-tour="sidebar-home"]) */
  target: string;
  title: string;
  content: React.ReactNode;
  /** Position du tooltip par rapport au spotlight */
  placement?: "top" | "bottom" | "left" | "right";
  /** À exécuter avant d'afficher cette étape (ex: ouvrir le menu mobile) */
  beforeStep?: () => void | Promise<void>;
  /** À exécuter quand l'utilisateur clique "Suivant" sur cette étape (ex: afficher l'onglet qu'on vient de montrer) */
  onLeaveToNext?: () => void | Promise<void>;
}

export interface ProductTourProps {
  /** Identifiant du tour (pour ne plus l'afficher après complétion) */
  tourId: string;
  steps: ProductTourStep[];
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
  /** Pour forcer l'étape courante de l'extérieur (optionnel) */
  currentStepIndex?: number;
  onStepChange?: (index: number) => void;
}

const OVERLAY_Z = 9998;
const SPOTLIGHT_PADDING = 8;
const ARROW_SIZE = 10;

function getTargetRect(selector: string): DOMRect | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  return el.getBoundingClientRect();
}

export function ProductTour({
  tourId,
  steps,
  isOpen,
  onComplete,
  onSkip,
  currentStepIndex: controlledStep,
  onStepChange,
}: ProductTourProps) {
  const [internalStep, setInternalStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipRect, setTooltipRect] = useState<{ width: number; height: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isControlled = controlledStep !== undefined;

  const currentIndex = isControlled ? controlledStep : internalStep;
  const setCurrentIndex = useCallback(
    (next: number) => {
      if (isControlled && onStepChange) onStepChange(next);
      else setInternalStep(next);
    },
    [isControlled, onStepChange]
  );

  const step = steps[currentIndex];
  const hasNext = currentIndex < steps.length - 1;
  const hasPrev = currentIndex > 0;

  const measureTarget = useCallback(() => {
    if (!step) return;
    const rect = getTargetRect(step.target);
    setTargetRect(rect);
    if (tooltipRef.current) {
      const tr = tooltipRef.current.getBoundingClientRect();
      setTooltipRect({ width: tr.width, height: tr.height });
    }
  }, [step?.target]);

  // Avant d'afficher l'étape : exécuter beforeStep puis mesurer
  useEffect(() => {
    if (!isOpen || !step) return;
    let cancelled = false;
    const run = async () => {
      if (step.beforeStep) await Promise.resolve(step.beforeStep());
      if (cancelled) return;
      // Laisser le temps au DOM de se mettre à jour (ex: changement d'onglet)
      requestAnimationFrame(() => {
        setTimeout(measureTarget, 80);
      });
    };
    run();
    return () => { cancelled = true; };
  }, [isOpen, currentIndex, step?.target, step?.beforeStep, measureTarget]);

  // Re-mesurer au scroll/resize
  useEffect(() => {
    if (!isOpen || !step) return;
    const onScrollOrResize = () => measureTarget();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    const interval = setInterval(measureTarget, 300);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      clearInterval(interval);
    };
  }, [isOpen, currentIndex, step?.target, measureTarget]);

  const handleNext = useCallback(async () => {
    const currentStepDef = steps[currentIndex];
    if (currentStepDef?.onLeaveToNext) {
      await Promise.resolve(currentStepDef.onLeaveToNext());
    }
    if (hasNext) setCurrentIndex(currentIndex + 1);
    else onComplete();
  }, [hasNext, currentIndex, setCurrentIndex, onComplete, steps]);

  const handlePrev = useCallback(() => {
    if (hasPrev) setCurrentIndex(currentIndex - 1);
  }, [hasPrev, currentIndex, setCurrentIndex]);

  if (!isOpen || steps.length === 0) return null;

  const placement = step?.placement ?? "right";
  const rect = targetRect;

  return (
    <div
      className="fixed inset-0 z-[9998]"
      style={{ zIndex: OVERLAY_Z }}
      aria-modal="true"
      role="dialog"
      aria-label="Visite guidée"
    >
      {/* Overlay sombre avec trou (spotlight) : tout devient noir sauf l'élément cible */}
      <div className="absolute inset-0 pointer-events-auto">
        {rect && (
          <div
            className="fixed rounded-xl pointer-events-none ring-2 ring-white ring-offset-2 ring-offset-transparent shadow-[0_0_0_9999px_rgba(0,0,0,0.88)] dark:ring-indigo-400"
            style={{
              left: Math.max(0, rect.left - SPOTLIGHT_PADDING),
              top: Math.max(0, rect.top - SPOTLIGHT_PADDING),
              width: rect.width + SPOTLIGHT_PADDING * 2,
              height: rect.height + SPOTLIGHT_PADDING * 2,
            }}
          />
        )}
      </div>

      {/* Tooltip avec flèche */}
      {step && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] w-80 max-w-[calc(100vw-2rem)] rounded-2xl border-2 border-indigo-200 dark:border-indigo-500/60 bg-white dark:bg-slate-800 shadow-2xl p-4 pointer-events-auto left-6 top-6"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">
              Étape {currentIndex + 1} / {steps.length}
            </span>
            <button
              type="button"
              onClick={onSkip}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Passer la visite"
            >
              <X size={18} />
            </button>
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">{step.title}</h3>
          <div className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed mb-5">
            {step.content}
          </div>

          {/* Flèche vers l'élément cible */}
          {rect && (
            <div
              className="absolute w-0 h-0 border-solid pointer-events-none"
              style={getArrowStyle(placement, rect)}
              aria-hidden
            />
          )}

          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-2">
              {hasPrev && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <ChevronLeft size={18} /> Précédent
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onSkip}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                Passer la visite
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/25 transition-colors"
              >
                {hasNext ? (
                  <>Suivant <ChevronRight size={18} /></>
                ) : (
                  "Terminer"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Positionnement du tooltip après mesure (pour éviter overflow) */}
      {step && rect && (
        <TooltipPositioner
          placement={placement}
          targetRect={rect}
          tooltipRef={tooltipRef}
        />
      )}
    </div>
  );
}

/** Repositionne le tooltip à côté du spotlight avec la flèche */
function getTooltipStyle(
  placement: "top" | "bottom" | "left" | "right",
  rect: DOMRect | null,
  tooltipRect: DOMRect | undefined
): React.CSSProperties {
  if (!rect) return { left: 24, top: 24 };

  const gap = 16;
  const padding = 16;
  const tw = 320;
  const th = tooltipRect?.height ?? 220;

  switch (placement) {
    case "right":
      return {
        left: Math.min(rect.right + gap, window.innerWidth - tw - padding),
        top: rect.top + rect.height / 2 - th / 2,
        maxHeight: window.innerHeight - padding * 2,
      };
    case "left":
      return {
        left: Math.max(padding, rect.left - tw - gap),
        top: rect.top + rect.height / 2 - th / 2,
        maxHeight: window.innerHeight - padding * 2,
      };
    case "bottom":
      return {
        left: rect.left + rect.width / 2 - tw / 2,
        top: Math.min(rect.bottom + gap, window.innerHeight - th - padding),
        maxHeight: window.innerHeight - padding * 2,
      };
    case "top":
      return {
        left: rect.left + rect.width / 2 - tw / 2,
        top: Math.max(padding, rect.top - th - gap),
        maxHeight: window.innerHeight - padding * 2,
      };
    default:
      return { left: rect.right + gap, top: rect.top };
  }
}

/** Flèche qui pointe vers l'élément cible (depuis le tooltip) */
function getArrowStyle(
  placement: "top" | "bottom" | "left" | "right",
  rect: DOMRect
): React.CSSProperties {
  const s = ARROW_SIZE;
  const border = `solid ${s}px transparent`;
  const color = "rgb(99 102 241)"; // indigo-500 pour être visible sur fond clair/sombre
  const common: React.CSSProperties = {
    position: "fixed",
    zIndex: 10001,
  };

  switch (placement) {
    case "right":
      // Tooltip à droite du target → flèche à la lisière target/tooltip, pointe vers la gauche (vers le target)
      return {
        ...common,
        left: rect.right,
        top: rect.top + rect.height / 2 - s,
        borderTop: border,
        borderBottom: border,
        borderLeft: `solid ${s}px ${color}`,
      };
    case "left":
      return {
        ...common,
        left: rect.left - s,
        top: rect.top + rect.height / 2 - s,
        borderTop: border,
        borderBottom: border,
        borderRight: `solid ${s}px ${color}`,
      };
    case "bottom":
      return {
        ...common,
        left: rect.left + rect.width / 2 - s,
        top: rect.top + rect.height,
        borderLeft: border,
        borderRight: border,
        borderTop: `solid ${s}px ${color}`,
      };
    case "top":
      return {
        ...common,
        left: rect.left + rect.width / 2 - s,
        top: rect.top - s,
        borderLeft: border,
        borderRight: border,
        borderBottom: `solid ${s}px ${color}`,
      };
    default:
      return common;
  }
}

/** Re-render du tooltip avec position calculée pour éviter débordement */
function TooltipPositioner({
  placement,
  targetRect,
  tooltipRef,
}: {
  placement: "top" | "bottom" | "left" | "right";
  targetRect: DOMRect;
  tooltipRef: React.RefObject<HTMLDivElement | null>;
}) {
  useEffect(() => {
    const el = tooltipRef.current;
    if (!el) return;
    const gap = 16;
    const padding = 16;
    const tw = el.offsetWidth;
    const th = el.offsetHeight;

    let left = 0;
    let top = 0;

    switch (placement) {
      case "right":
        left = targetRect.right + gap;
        top = targetRect.top + targetRect.height / 2 - th / 2;
        if (left + tw > window.innerWidth - padding) left = window.innerWidth - tw - padding;
        if (top < padding) top = padding;
        if (top + th > window.innerHeight - padding) top = window.innerHeight - th - padding;
        break;
      case "left":
        left = targetRect.left - tw - gap;
        top = targetRect.top + targetRect.height / 2 - th / 2;
        if (left < padding) left = padding;
        if (top < padding) top = padding;
        if (top + th > window.innerHeight - padding) top = window.innerHeight - th - padding;
        break;
      case "bottom":
        left = targetRect.left + targetRect.width / 2 - tw / 2;
        top = targetRect.bottom + gap;
        if (top + th > window.innerHeight - padding) top = targetRect.top - th - gap;
        if (left < padding) left = padding;
        if (left + tw > window.innerWidth - padding) left = window.innerWidth - tw - padding;
        break;
      case "top":
        left = targetRect.left + targetRect.width / 2 - tw / 2;
        top = targetRect.top - th - gap;
        if (top < padding) top = targetRect.bottom + gap;
        if (left < padding) left = padding;
        if (left + tw > window.innerWidth - padding) left = window.innerWidth - tw - padding;
        break;
    }

    el.style.position = "fixed";
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  });
  return null;
}
